/**
 * Core API Client
 * Handles HTTP requests with authentication, error handling, and interceptors
 */

import { API_BASE_URL, DEFAULT_HEADERS, REQUEST_TIMEOUT, LOGIN_URL } from './config.js';
import { getAuthorizationHeader, clearAuth, isTokenExpired, getAuthToken } from './auth.js';
import { isPublicApiEndpoint } from '../publicRoutes.js';
import { dispatchBillingUpgradeRequired, dispatchTrialNotStarted } from '../companyBillingGate.js';
import {
  buildImageUploadNetworkMessage,
  buildImageUploadTimeoutMessage,
  buildImageUploadTooLargeMessage,
  ensureImageUploadLimitMb,
} from '../imageUploadLimits.js';
import { ApiError, NetworkError, createApiError } from './errors.js';
import { interceptors } from './interceptors.js';

/**
 * Create a timeout promise
 * @param {number} ms - Timeout in milliseconds
 * @returns {Promise<Error>}
 */
const createTimeout = (ms) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new NetworkError(`Request timeout after ${ms}ms`)), ms);
  });
};

/**
 * Parse API response
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed response data
 */
const parseResponse = async (response) => {
  const contentType = response.headers.get('content-type');
  
  // Handle non-JSON responses (e.g. reverse-proxy 413 HTML pages during file uploads)
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    if (!response.ok) {
      let message = text || 'Invalid response format';
      if (response.status === 413 || /entity too large/i.test(message)) {
        await ensureImageUploadLimitMb();
        message = buildImageUploadTooLargeMessage();
      } else if (message.length > 240 || /<html/i.test(message)) {
        message = `Request failed (${response.status} ${response.statusText || 'Error'})`;
      }
      throw createApiError(response, { message, error: message });
    }
    return text ? { raw: text } : null;
  }

  let data;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      if (response.status === 413) {
        await ensureImageUploadLimitMb();
      }
      const message =
        response.status === 413
          ? buildImageUploadTooLargeMessage()
          : `Request failed (${response.status} ${response.statusText || 'Error'})`;
      throw createApiError(response, { message, error: message });
    }
    throw new NetworkError('Invalid response from server');
  }

  // Handle error responses
  if (!response.ok) {
    throw createApiError(response, data);
  }

  // Handle standardized API response format
  // Expected: { success: true, message: "...", data: {...} }
  if (data.success !== undefined) {
    return data.data !== undefined ? data.data : data;
  }

  // Fallback: return data as-is
  return data;
};

/**
 * Build full URL from endpoint
 * @param {string} endpoint - API endpoint
 * @returns {string} Full URL
 */
const buildUrl = (endpoint) => {
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }
  
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${normalizedEndpoint}`;
};

/**
 * Build headers for request
 * @param {Object} customHeaders - Custom headers to merge
 * @returns {Object} Headers object
 */
const buildHeaders = (customHeaders = {}, endpoint = '') => {
  const headers = { ...DEFAULT_HEADERS, ...customHeaders };
  const isPublic = isPublicApiEndpoint(endpoint);

  if (!isPublic) {
    const token = getAuthToken();
    if (token && !isTokenExpired(token)) {
      const authHeader = getAuthorizationHeader();
      if (authHeader) {
        headers['Authorization'] = authHeader;
      }
    } else if (token && isTokenExpired(token)) {
      clearAuth();
    }
  }

  return headers;
};

/**
 * Make API request with interceptors and error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Parsed response data
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = buildUrl(endpoint);
  
  // Build initial config
  let config = {
    ...options,
    headers: buildHeaders(options.headers, endpoint),
  };

  // Execute request interceptors
  try {
    config = await interceptors.executeRequestInterceptors(config, { endpoint, url });
  } catch (error) {
    throw new ApiError('Request interceptor failed', 0, null, null);
  }

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  // Let the browser set multipart boundary when uploading FormData
  const isUploadRequest = typeof FormData !== 'undefined' && config.body instanceof FormData;
  if (isUploadRequest) {
    const headers = { ...config.headers };
    delete headers['Content-Type'];
    config = { ...config, headers };
  }

  try {
    // Make request with timeout
    const response = await Promise.race([
      fetch(url, {
        ...config,
        signal: controller.signal,
      }),
      createTimeout(REQUEST_TIMEOUT),
    ]);

    clearTimeout(timeoutId);

    // Execute response interceptors
    const processedResponse = await interceptors.executeResponseInterceptors(response, { endpoint, url });

    // Handle 403 billing upgrade gate — keep session, send user to checkout
    if (processedResponse.status === 403) {
      const contentType = processedResponse.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          const data = await processedResponse.clone().json();
          if (data.code === 'BILLING_UPGRADE_REQUIRED') {
            dispatchBillingUpgradeRequired();
            throw createApiError(processedResponse, data);
          }
          if (data.code === 'TRIAL_NOT_STARTED') {
            dispatchTrialNotStarted();
            throw createApiError(processedResponse, data);
          }
          // Company deactivated after the token was issued — end the stale session
          if (data.code === 'COMPANY_ACCOUNT_INACTIVE') {
            clearAuth();
            setTimeout(() => {
              window.location.href = LOGIN_URL;
            }, 1000);
            throw createApiError(processedResponse, data);
          }
        } catch (error) {
          if (error instanceof ApiError) {
            throw error;
          }
        }
      }
    }

    // Handle 401 Unauthorized - token expired or invalid (skip redirect on public endpoints)
    if (processedResponse.status === 401) {
      const isPublic = isPublicApiEndpoint(endpoint);
      if (!isPublic) {
        clearAuth();
        setTimeout(() => {
          window.location.href = LOGIN_URL;
        }, 1000);
        throw createApiError(processedResponse, {
          message: 'Your session has expired. Please log in again.',
          error: 'Unauthorized'
        });
      }
    }

    // Parse response (this will handle 403 and other errors with proper messages)
    return await parseResponse(processedResponse);
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort (timeout) and network failures — must throw, not return (otherwise thunks fulfill with Error)
    if (error.name === 'AbortError' || error instanceof NetworkError) {
      if (isUploadRequest) {
        await ensureImageUploadLimitMb();
      }
      const networkError =
        error instanceof NetworkError
          ? error
          : new NetworkError(
              isUploadRequest
                ? buildImageUploadTimeoutMessage()
                : 'Request timeout or network error',
              error
            );
      const processed = await interceptors.executeErrorInterceptors(networkError, { endpoint, url });
      throw processed;
    }

    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      if (isUploadRequest) {
        await ensureImageUploadLimitMb();
      }
      const networkError = new NetworkError(
        isUploadRequest ? buildImageUploadNetworkMessage() : 'Network error: Unable to connect to the server',
        error
      );
      const processed = await interceptors.executeErrorInterceptors(networkError, { endpoint, url });
      throw processed;
    }

    const processedError = await interceptors.executeErrorInterceptors(error, { endpoint, url });
    throw processedError;
  }
};

/**
 * API Client Methods
 */
export const api = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Object>} Response data
   */
  get: (endpoint, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'GET',
    });
  },

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Object>} Response data
   */
  post: (endpoint, body, options = {}) => {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    return apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: isFormData ? body : JSON.stringify(body),
    });
  },

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Object>} Response data
   */
  put: (endpoint, body, options = {}) => {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    return apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: isFormData ? body : JSON.stringify(body),
    });
  },

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} body - Request body
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Object>} Response data
   */
  patch: (endpoint, body, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Additional fetch options
   * @returns {Promise<Object>} Response data
   */
  delete: (endpoint, options = {}) => {
    return apiRequest(endpoint, {
      ...options,
      method: 'DELETE',
    });
  },
};

export default api;
